import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { getPhotoUrl } from "@/utils/photoUrl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Users,
  Home,
  Target,
  Trophy,
  Shuffle,
  Share2,
} from "lucide-react";
import { useLanguage } from "@/i18n";
import TeamCard from "@/components/gameday/TeamCard";
import SwapDialog from "@/components/gameday/SwapDialog";
import PlayerSelectionCard from "@/components/gameday/PlayerSelectionCard";
import GenerateTeamsPanel from "@/components/gameday/GenerateTeamsPanel";
import WinProbabilityChart from "@/components/gameday/WinProbabilityChart";

export default function GameDayPage({ evaluator }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [players, setPlayers] = useState([]);
  const [averages, setAverages] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]); // [{player_id, profile_type}]
  const [generatedTeams, setGeneratedTeams] = useState(null);
  const [editedTeamOrders, setEditedTeamOrders] = useState({}); // {teamNumber: [player1, player2, ...]}
  const [gameDate, setGameDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [recentGameDays, setRecentGameDays] = useState([]);
  const [location, setLocation] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  
  // Estados para troca de jogadores
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [selectedPlayerForSwap, setSelectedPlayerForSwap] = useState(null); // {player, teamNumber, index}
  const [targetTeamForSwap, setTargetTeamForSwap] = useState(null);
  const [targetPlayerForSwap, setTargetPlayerForSwap] = useState(null);

  // Nomes das posições no campo de vôlei (1-6)
  const COURT_POSITIONS = {
    1: { name: "Levantador", zone: "Fundo", color: "bg-cyan-500/20 text-cyan-300" },
    2: { name: "Ponteiro", zone: "Rede", color: "bg-orange-500/20 text-orange-300" },
    3: { name: "Central", zone: "Rede", color: "bg-purple-500/20 text-purple-300" },
    4: { name: "Oposto", zone: "Rede", color: "bg-red-500/20 text-red-300" },
    5: { name: "Ponteiro", zone: "Fundo", color: "bg-orange-500/20 text-orange-300" },
    6: { name: "Central", zone: "Fundo", color: "bg-purple-500/20 text-purple-300" }
  };

  // Função para mover jogador para cima/baixo
  const movePlayer = (teamNumber, fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    
    // Get current order for this team
    const currentOrder = editedTeamOrders[teamNumber] || 
      generatedTeams.find(t => t.team_number === teamNumber)?.players || [];
    
    // Don't move if out of bounds
    if (toIndex < 0 || toIndex >= currentOrder.length) return;
    
    // Create new array and swap
    const newOrder = [...currentOrder];
    [newOrder[fromIndex], newOrder[toIndex]] = [newOrder[toIndex], newOrder[fromIndex]];
    
    // Update state
    setEditedTeamOrders(prev => ({
      ...prev,
      [teamNumber]: newOrder
    }));
  };

  // Função para iniciar a troca de jogador
  const startSwapPlayer = (player, teamNumber, index) => {
    setSelectedPlayerForSwap({ player, teamNumber, index });
    setTargetTeamForSwap(null);
    setTargetPlayerForSwap(null);
    setSwapDialogOpen(true);
  };

  // Função para executar a troca de jogadores entre times
  const executeSwap = () => {
    if (!selectedPlayerForSwap || !targetTeamForSwap || !targetPlayerForSwap) {
      toast.error("Selecione um jogador para trocar");
      return;
    }

    const sourceTeam = selectedPlayerForSwap.teamNumber;
    const targetTeam = parseInt(targetTeamForSwap);
    
    // Get current orders
    const sourceOrder = [...(editedTeamOrders[sourceTeam] || 
      generatedTeams.find(t => t.team_number === sourceTeam)?.players || [])];
    const targetOrder = [...(editedTeamOrders[targetTeam] || 
      generatedTeams.find(t => t.team_number === targetTeam)?.players || [])];
    
    // Find indices
    const sourceIndex = sourceOrder.findIndex(p => p.id === selectedPlayerForSwap.player.id);
    const targetIndex = targetOrder.findIndex(p => p.id === targetPlayerForSwap);
    
    if (sourceIndex === -1 || targetIndex === -1) {
      toast.error("Erro ao encontrar jogadores");
      return;
    }
    
    // Swap players
    const tempPlayer = sourceOrder[sourceIndex];
    sourceOrder[sourceIndex] = targetOrder[targetIndex];
    targetOrder[targetIndex] = tempPlayer;
    
    // Update state
    setEditedTeamOrders(prev => ({
      ...prev,
      [sourceTeam]: sourceOrder,
      [targetTeam]: targetOrder
    }));
    
    // Close dialog and reset
    setSwapDialogOpen(false);
    setSelectedPlayerForSwap(null);
    setTargetTeamForSwap(null);
    setTargetPlayerForSwap(null);
    
    toast.success("Jogadores trocados com sucesso!");
  };

  // Obter jogadores com ordem editada ou original
  const getTeamPlayers = (team) => {
    return editedTeamOrders[team.team_number] || team.players || [];
  };

  const fetchData = useCallback(async () => {
    try {
      const [playersRes, averagesRes, gamedaysRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/players/all/averages`),
        axios.get(`${API}/gamedays`)
      ]);
      setPlayers(playersRes.data);
      setAverages(averagesRes.data);
      setRecentGameDays(gamedaysRes.data.slice(0, 5));
      
      // Auto-select players from game registration if from_game param exists
      const fromGameId = searchParams.get('from_game');
      if (fromGameId) {
        try {
          const gameRes = await axios.get(`${API}/game-registrations/${fromGameId}`);
          const game = gameRes.data;
          const regPlayers = game.registered_players || [];
          if (regPlayers.length > 0) {
            const autoSelected = regPlayers.map(rp => ({
              player_id: rp.player_id,
              profile_type: "jogador"
            }));
            setSelectedPlayers(autoSelected);
            if (game.date) setGameDate(game.date);
            if (game.location) setLocation(game.location);
            if (game.time) setStartTime(game.time);
            toast.success(`${regPlayers.length} jogadores carregados das inscricoes`);
          }
        } catch (err) {
          console.error("Error loading game registrations:", err);
        }
      }
    } catch (error) {
      toast.error(t('toasts.errorLoadingData'));
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getPlayerAverage = (playerId) => {
    const avg = averages.find(a => a.player_id === playerId);
    if (!avg || avg.total_evaluations === 0) return 0;
    const values = Object.values(avg.averages);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const getPlayerName = (player) => {
    if (player.first_name) return `${player.first_name} ${player.last_name}`.trim();
    return (player.name || '').trim();
  };

  const normalizeName = (str) => {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const [namesInput, setNamesInput] = useState("");

  const handleSelectByNames = () => {
    if (!namesInput.trim()) {
      toast.error(t('toasts.pasteNamesFirst'));
      return;
    }

    const rawParts = namesInput
      .split(/[\n,;]+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);

    if (!rawParts.length) {
      toast.error(t('toasts.noValidNames'));
      return;
    }

    let foundCount = 0;
    const notFound = [];
    let currentSelected = [...selectedPlayers];

    rawParts.forEach(part => {
      const normalized = normalizeName(part);

      // Busca mais flexível: primeiro tenta nome exato, depois primeiro nome, depois contém
      const match = players.find(player => {
        const fullName = normalizeName(getPlayerName(player));
        const firstName = normalizeName(player.first_name || '');
        const lastName = normalizeName(player.last_name || '');
        
        // 1. Correspondência exata com nome completo
        if (fullName === normalized) return true;
        
        // 2. Correspondência exata com primeiro nome
        if (firstName === normalized) return true;
        
        // 3. Correspondência exata com sobrenome
        if (lastName === normalized) return true;
        
        // 4. Nome completo contém o termo buscado
        if (fullName.includes(normalized)) return true;
        
        // 5. Primeiro nome começa com o termo buscado
        if (firstName.startsWith(normalized)) return true;
        
        return false;
      });

      if (match) {
        const alreadySelected = currentSelected.some(sp => sp.player_id === match.id);
        if (!alreadySelected) {
          if (currentSelected.length >= 18) {
            return;
          }
          const defaultProfile = match.position === 'Levantador' ? 'levantador' : 'jogador';
          currentSelected.push({ player_id: match.id, profile_type: defaultProfile });
          foundCount += 1;
        }
      } else {
        notFound.push(part);
      }
    });

    setSelectedPlayers(currentSelected);

    if (foundCount > 0) {
      toast.success(`${foundCount} ${t('toasts.playersSelected')}`);
    }
    if (notFound.length > 0) {
      toast.error(`${t('toasts.namesNotFound')} ${notFound.join(', ')}`);
    }
  };

  const isPlayerSelected = (playerId) => {
    return selectedPlayers.some(sp => sp.player_id === playerId);
  };

  const getPlayerSelection = (playerId) => {
    return selectedPlayers.find(sp => sp.player_id === playerId);
  };

  const togglePlayer = (playerId, profileType = null) => {
    const player = players.find(p => p.id === playerId);
    
    if (isPlayerSelected(playerId)) {
      setSelectedPlayers(selectedPlayers.filter(sp => sp.player_id !== playerId));
    } else {
      if (selectedPlayers.length >= 18) {
        toast.error(t('toasts.maxPlayersSelected'));
        return;
      }
      // For setters, default to "levantador" profile
      const defaultProfile = player?.position === 'Levantador' ? 'levantador' : 'jogador';
      setSelectedPlayers([...selectedPlayers, { player_id: playerId, profile_type: profileType || defaultProfile }]);
    }
  };

  const changePlayerProfile = (playerId, newProfile) => {
    setSelectedPlayers(selectedPlayers.map(sp => 
      sp.player_id === playerId ? { ...sp, profile_type: newProfile } : sp
    ));
  };

  const getSelectedLevantadores = () => {
    // Count only setters playing AS levantadores
    return selectedPlayers.filter(sp => {
      const player = players.find(p => p.id === sp.player_id);
      return player?.position === 'Levantador' && sp.profile_type === 'levantador';
    }).length;
  };

  const handleGenerateTeams = async () => {
    if (selectedPlayers.length !== 18) {
      toast.error(t('toasts.selectExactly18'));
      return;
    }

    const levantadores = getSelectedLevantadores();
    if (levantadores < 3) {
      toast.error(`${t('toasts.selectMinSetters')} ${levantadores}`);
      return;
    }

    setIsGenerating(true);
    setIsPublished(false);
    try {
      // Preview only - don't save yet
      const response = await axios.post(`${API}/gameday/preview`, {
        player_selections: selectedPlayers,
        player_ids: selectedPlayers.map(sp => sp.player_id),
        date: gameDate
      });
      setGeneratedTeams(response.data.teams);
      toast.success(t('toasts.teamsGenerated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorGeneratingTeams'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublishTeams = async () => {
    if (!generatedTeams || generatedTeams.length === 0) {
      toast.error(t('toasts.generateTeamsFirst'));
      return;
    }

    setIsPublishing(true);
    try {
      // Preparar os times com a ordem editada (se houver)
      const customTeams = generatedTeams.map(team => {
        // Usa a ordem editada se existir, senão usa a ordem original
        const players = editedTeamOrders[team.team_number] || team.players || [];
        return {
          team_number: team.team_number,
          players: players.map(p => ({ id: p.id, player_id: p.id }))
        };
      });

      await axios.post(`${API}/gameday`, {
        player_selections: selectedPlayers,
        player_ids: selectedPlayers.map(sp => sp.player_id),
        date: gameDate,
        location: location || null,
        start_time: startTime || null,
        end_time: endTime || null,
        custom_teams: customTeams  // Enviar a ordem personalizada
      });
      setIsPublished(true);
      setEditedTeamOrders({}); // Limpar as edições após publicar
      toast.success(t('toasts.teamsPublished'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorPublishingTeams'));
    } finally {
      setIsPublishing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  const getTeamColor = (teamNumber) => {
    const colors = ['border-blue-500 bg-blue-500/10', 'border-green-500 bg-green-500/10', 'border-purple-500 bg-purple-500/10'];
    return colors[teamNumber - 1] || 'border-gray-500';
  };

  const getTeamGradient = (teamNumber) => {
    const gradients = ['from-blue-500/20 to-blue-600/5', 'from-green-500/20 to-green-600/5', 'from-purple-500/20 to-purple-600/5'];
    return gradients[teamNumber - 1] || 'from-gray-500/20';
  };

  const getTeamAccent = (teamNumber) => {
    const accents = ['#3b82f6', '#22c55e', '#a855f7'];
    return accents[teamNumber - 1] || '#6b7280';
  };

  // Função para obter o nome do jogador (yankee ou primeiro nome)
  const getPlayerDisplayName = (player) => {
    if (!player) return '';
    return player.yankee || player.first_name || player.name || 'Jogador';
  };

  // Função para compartilhar times no WhatsApp
  const shareOnWhatsApp = () => {
    if (!generatedTeams || generatedTeams.length === 0) {
      toast.error(t('toasts.noTeamsToShare'));
      return;
    }

    let message = `🏐 *TIMES DO DIA - ${gameDate}*\n`;
    if (location) message += `📍 ${location}\n`;
    if (startTime) message += `⏰ ${startTime}${endTime ? ` às ${endTime}` : ''}\n`;
    message += '\n';

    generatedTeams.forEach((team) => {
      // Formatar número do time com 2 dígitos (01, 02, 03)
      const teamNumber = String(team.team_number).padStart(2, '0');
      message += `*TIME ${teamNumber}*\n`;
      
      // Obter a ordem dos jogadores (editada ou original)
      const teamPlayers = editedTeamOrders[team.team_number] || team.players || [];
      
      // Listar jogadores de 1 a 6 apenas com nome (SEM média/ranking)
      teamPlayers.forEach((player, index) => {
        // Priorizar first_name, depois name (primeiro nome apenas)
        // Ignorar yankee pois pode conter classificações como "C+", "B-"
        let playerName = 'Jogador';
        if (player?.first_name) {
          playerName = player.first_name;
        } else if (player?.name) {
          // Pegar apenas o primeiro nome
          playerName = player.name.split(' ')[0];
        }
        message += `${index + 1} ${playerName}\n`;
      });
      
      message += '\n';
    });

    // Codificar a mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    
    // Abrir WhatsApp
    window.open(whatsappUrl, '_blank');
    toast.success(t('toasts.openingWhatsApp'));
  };

  // Helper to assign players to court positions based on list order
  // A ordem na lista (1-6) corresponde diretamente às posições no campo
  const assignCourtPositions = (teamPlayers) => {
    if (!teamPlayers || teamPlayers.length === 0) return {};

    // Simplesmente mapeia a ordem da lista para as posições do campo
    // Posição na lista -> Posição no campo:
    // 1 (índice 0) -> p1 (Defensor Direito - Fundo Direito)
    // 2 (índice 1) -> p2 (Ponteiro - Rede Direita)
    // 3 (índice 2) -> p3 (Central - Rede Centro)
    // 4 (índice 3) -> p4 (Oposto - Rede Esquerda)
    // 5 (índice 4) -> p5 (Defensor Esquerdo - Fundo Esquerdo)
    // 6 (índice 5) -> p6 (Levantador - Fundo Centro)

    return { 
      p1: teamPlayers[0] || null,  // Defensor Direito
      p2: teamPlayers[1] || null,  // Ponteiro
      p3: teamPlayers[2] || null,  // Central
      p4: teamPlayers[3] || null,  // Oposto
      p5: teamPlayers[4] || null,  // Defensor Esquerdo
      p6: teamPlayers[5] || null   // Levantador
    };
  };

  // Group players by position
  const playersByPosition = {};
  players.forEach(player => {
    const pos = player.position || 'Outros';
    if (!playersByPosition[pos]) playersByPosition[pos] = [];
    playersByPosition[pos].push(player);
  });

  // Prepare probability chart data
  const probabilityData = generatedTeams ? generatedTeams.map(team => ({
    name: `Time ${team.team_number}`,
    probability: team.win_probability,
    fill: getTeamAccent(team.team_number)
  })) : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  Times do Dia
                </h1>
                <p className="text-sm text-muted-foreground">
                  Selecione 18 jogadores para gerar 3 times equilibrados
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={selectedPlayers.length === 18 ? "default" : "secondary"}>
                {selectedPlayers.length}/18 selecionados
              </Badge>
              <Badge variant={getSelectedLevantadores() >= 3 ? "default" : "destructive"}>
                {getSelectedLevantadores()}/3 como levantador
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Player Selection */}
          <div className="lg:col-span-2 space-y-6">
            <PlayerSelectionCard
              isLoading={isLoading}
              playersByPosition={playersByPosition}
              isPlayerSelected={isPlayerSelected}
              getPlayerSelection={getPlayerSelection}
              getPlayerAverage={getPlayerAverage}
              togglePlayer={togglePlayer}
              changePlayerProfile={changePlayerProfile}
              getScoreColor={getScoreColor}
              getPlayerName={getPlayerName}
              namesInput={namesInput}
              setNamesInput={setNamesInput}
              handleSelectByNames={handleSelectByNames}
            />
          </div>

          {/* Generate Teams Panel */}
          <GenerateTeamsPanel
            selectedPlayers={selectedPlayers}
            getSelectedLevantadores={getSelectedLevantadores}
            gameDate={gameDate}
            setGameDate={setGameDate}
            location={location}
            setLocation={setLocation}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
            handleGenerateTeams={handleGenerateTeams}
            isGenerating={isGenerating}
            generatedTeams={generatedTeams}
            isPublished={isPublished}
            handlePublishTeams={handlePublishTeams}
            isPublishing={isPublishing}
            recentGameDays={recentGameDays}
            setGeneratedTeams={setGeneratedTeams}
          />
        </div>

        {/* Generated Teams - New Design */}
        {generatedTeams && (
          <div className="mt-8 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Trophy className="h-6 w-6 text-yellow-400" />
                Times Gerados - {gameDate}
              </h2>
              <Button
                onClick={shareOnWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white font-bold shadow-[0_0_20px_rgba(34,197,94,0.3)]"
                data-testid="share-whatsapp-btn"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Compartilhar no WhatsApp
              </Button>
            </div>

            <WinProbabilityChart probabilityData={probabilityData} />
            
            {/* Teams in Columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {generatedTeams.map((team) => {
                const teamPlayers = getTeamPlayers(team);
                const positions = assignCourtPositions(teamPlayers);
                const maxAverage = teamPlayers?.length
                  ? Math.max(...teamPlayers.map(p => p.average))
                  : 0;
                return (
                  <TeamCard
                    key={team.team_number}
                    team={team}
                    teamPlayers={teamPlayers}
                    positions={positions}
                    maxAverage={maxAverage}
                    teamNumber={team.team_number}
                    getTeamColor={getTeamColor}
                    getTeamGradient={getTeamGradient}
                    getTeamAccent={getTeamAccent}
                    movePlayer={movePlayer}
                    startSwapPlayer={startSwapPlayer}
                    shareOnWhatsApp={shareOnWhatsApp}
                    generatedTeams={generatedTeams}
                  />
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Swap Dialog */}
      <SwapDialog
        open={swapDialogOpen}
        onOpenChange={setSwapDialogOpen}
        selectedPlayerForSwap={selectedPlayerForSwap}
        generatedTeams={generatedTeams}
        editedTeamOrders={editedTeamOrders}
        targetTeamForSwap={targetTeamForSwap}
        setTargetTeamForSwap={setTargetTeamForSwap}
        targetPlayerForSwap={targetPlayerForSwap}
        setTargetPlayerForSwap={setTargetPlayerForSwap}
        executeSwap={executeSwap}
      />

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/gameday')}
          className="flex flex-col items-center gap-1 h-auto py-2 text-primary"
        >
          <Shuffle className="h-5 w-5" />
          <span className="text-xs">Times</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
